<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('Exit Screen') }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="mb-4">
                <v-card-text class="d-flex align-center">
                  <div class="text-subtitle-1 font-weight-medium">
                    {{ $t('Page Status') }}
                  </div>
                  <v-spacer></v-spacer>
                  <v-switch
                    v-model="formData.pages.exit.active"
                    :label="
                      formData.pages.exit.active ? $t('Active') : $t('Inactive')
                    "
                    dense
                    hide-details
                    class="mt-0"
                    @change="updateForm"
                  ></v-switch>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-expansion-panels v-model="openPanel">
                <!-- Content Section Panel -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('Content') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-model="formData.pages.exit.content.title"
                      dense
                      outlined
                      :rules="[required]"
                      :label="$t('Title')"
                      @input="updateForm"
                    />

                    <v-text-field
                      v-model="formData.pages.exit.content.subtitle"
                      dense
                      outlined
                      :rules="[required]"
                      :label="$t('Subtitle')"
                      @input="updateForm"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Background Image Panel -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('Background Image') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12">
                        <v-input
                          :value="formData.pages.exit.content.image"
                          :rules="[required]"
                        />
                        <gallery
                          :key="formData.pages.exit.content.image"
                          button-text="Background image"
                          folder="booking-engine"
                          :compress="false"
                          :cols="6"
                          :thumbnails="true"
                          readonly
                          :images="
                            formData.pages.exit.content.image
                              ? [formData.pages.exit.content.image]
                              : []
                          "
                          :on-change="afterImageUpload"
                          :uploadable="true"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Settings Panel -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('Settings') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-checkbox
                      v-model="formData.pages.exit.content.askForEmail"
                      :label="$t('Ask for email input')"
                      dense
                      class="mt-0 pt-0"
                      @change="updateForm"
                    ></v-checkbox>

                    <v-checkbox
                      v-model="formData.pages.exit.content.offerDiscount"
                      :label="$t('Offer Discount Coupon')"
                      dense
                      class="mt-0 pt-0"
                      @change="updateForm"
                    ></v-checkbox>

                    <template v-if="formData.pages.exit.content.offerDiscount">
                      <v-text-field
                        v-model="formData.pages.exit.content.discountSubtitle"
                        dense
                        outlined
                        :rules="[required]"
                        :label="$t('Discount Subtitle')"
                        @input="updateForm"
                      />

                      <v-text-field
                        v-model.number="
                          formData.pages.exit.content.discountAmount
                        "
                        dense
                        outlined
                        type="number"
                        min="0"
                        max="100"
                        :rules="[required]"
                        :label="$t('Discount Amount (%)')"
                        @input="updateForm"
                      />

                      <v-text-field
                        v-model="formData.pages.exit.content.couponCode"
                        dense
                        outlined
                        :rules="[required]"
                        :label="$t('Coupon Code')"
                        @input="updateForm"
                      />
                    </template>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>

            <v-col cols="12" md="6">
              <div class="preview-container">
                <preview-frame
                  :url="previewUrl"
                  :needs-refresh="needsRefresh"
                  class="preview-frame"
                  @refresh="refreshPreview"
                  @load="onFrameLoad"
                />
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import Gallery from 'components/common/gallery.vue'
import PreviewFrame from '../components/preview-frame.vue'
import axios from 'axios'

export default {
  name: 'ExitScreenSettings',

  components: {
    Gallery,
    PreviewFrame,
  },

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      needsRefresh: false,
      isFrameLoading: true,
      openPanel: 0,
      formData: {
        name: this.bookingEngineSetting.name,
        pages: {
          exit: {
            active: this.bookingEngineSetting.pages?.exit?.active || false,
            content: {
              title:
                this.bookingEngineSetting.pages?.exit?.content?.title ||
                'Before You Go...',
              subtitle:
                this.bookingEngineSetting.pages?.exit?.content?.subtitle ||
                'Get 10% off your first dream vacation rental!',
              image:
                this.bookingEngineSetting.pages?.exit?.content?.image || null,
              askForEmail:
                this.bookingEngineSetting.pages?.exit?.content?.askForEmail ||
                false,
              offerDiscount:
                this.bookingEngineSetting.pages?.exit?.content?.offerDiscount ||
                true,
              discountAmount:
                this.bookingEngineSetting.pages?.exit?.content
                  ?.discountAmount || 10,
              discountSubtitle:
                this.bookingEngineSetting.pages?.exit?.content
                  ?.discountSubtitle || 'Use this code at checkout:',
              couponCode:
                this.bookingEngineSetting.pages?.exit?.content?.couponCode ||
                '10off',
            },
          },
        },
      },
    }
  },

  computed: {
    previewUrl() {
      const baseUrl =
        process.env.NODE_ENV === 'development'
          ? 'http://localhost:8081'
          : `https://${this.bookingEngineSetting.name}.bookingsboom.com`
      return `${baseUrl}/?exit-intent=true`
    },
  },

  watch: {
    'formData.pages.exit.active': {
      handler(newVal) {
        this.refreshPreview()
      },
    },
    bookingEngineSetting: {
      handler(newVal) {
        this.formData = {
          name: newVal.name,
          pages: {
            exit: {
              active: newVal.pages?.exit?.active || false,
              content: {
                title: newVal.pages?.exit?.content?.title || 'Before You Go...',
                subtitle:
                  newVal.pages?.exit?.content?.subtitle ||
                  'Get 10% off your first dream vacation rental!',
                image: newVal.pages?.exit?.content?.image || null,
                askForEmail: newVal.pages?.exit?.content?.askForEmail || false,
                offerDiscount:
                  newVal.pages?.exit?.content?.offerDiscount || true,
                discountAmount:
                  newVal.pages?.exit?.content?.discountAmount || 10,
                discountSubtitle:
                  newVal.pages?.exit?.content?.discountSubtitle ||
                  'Use this code at checkout:',
                couponCode: newVal.pages?.exit?.content?.couponCode || '10off',
              },
            },
          },
        }
      },
      deep: true,
    },
  },

  methods: {
    updateForm() {
      console.log('updateForm called, validating form...')
      if (!this.$refs.form || this.$refs.form.validate()) {
        console.log(
          'Form valid, emitting update with image:',
          this.formData.pages.exit.content.image
        )
        this.$emit('update:setting', {
          ...this.bookingEngineSetting,
          pages: {
            ...this.bookingEngineSetting.pages,
            exit: this.formData.pages.exit,
          },
        })
        this.needsRefresh = true
      }
    },

    afterImageUpload(imgs) {
      console.log('Image upload completed:', imgs)
      if (!imgs || !imgs.length) {
        console.error('No image received from upload')
        return
      }

      // Ensure we're getting a string URL
      const imageUrl = typeof imgs[0] === 'string' ? imgs[0] : imgs[0]?.url
      if (!imageUrl) {
        console.error('Invalid image URL format:', imgs[0])
        return
      }

      this.formData.pages.exit.content.image = imageUrl
      console.log(
        'Updated formData image:',
        this.formData.pages.exit.content.image
      )

      // Force a deep clone of the data to ensure reactivity
      const updatedSettings = JSON.parse(
        JSON.stringify({
          ...this.bookingEngineSetting,
          pages: {
            ...this.bookingEngineSetting.pages,
            exit: {
              ...this.formData.pages.exit,
              content: {
                ...this.formData.pages.exit.content,
                image: imageUrl,
              },
            },
          },
        })
      )

      console.log('Emitting settings update with:', updatedSettings)
      this.$emit('update:setting', updatedSettings)
      this.needsRefresh = true
      this.refreshPreview()
    },

    validate() {
      return this.$refs.form.validate()
    },

    async refreshPreview() {
      console.log('refreshPreview called, validating form...')
      if (!this.$refs.form || this.$refs.form.validate()) {
        try {
          this.isFrameLoading = true
          const payload = {
            name: this.bookingEngineSetting.name,
            pages: {
              ...this.bookingEngineSetting.pages,
              exit: {
                ...this.formData.pages.exit,
                content: {
                  ...this.formData.pages.exit.content,
                  image: this.formData.pages.exit.content.image,
                },
              },
            },
          }

          console.log('Sending API request with payload:', payload)

          await axios.patch(
            `/api/booking-engine-settings/${this.bookingEngineSetting.id}`,
            payload
          )

          console.log('API request successful')
          this.needsRefresh = false
        } catch (error) {
          console.error(
            'Failed to save settings:',
            error.response?.data || error
          )
          this.$store.dispatch('snackbar/showSnackbar', {
            message: this.$t('Failed to save settings'),
            color: 'error',
          })
        }
      }
    },

    onFrameLoad() {
      this.isFrameLoading = false
    },
  },
}
</script>

<style scoped>
.preview-container {
  position: relative;
  height: calc(100vh - 300px);
  max-height: 700px;
  overflow: hidden;
}

.preview-frame {
  height: 100%;
  width: 100%;
}

iframe {
  border: none;
  height: 100%;
  width: 100%;
}

.v-expansion-panels {
  height: calc(100vh - 300px);
  max-height: 700px;
  overflow-y: auto;
}
</style>
